import { z } from 'zod';
import { SalesOrderItemType } from '../../constants/sales-order-item-type.js';
import { SAMPLE_PURPOSES } from '../../constants/sample-purpose.js';
import { ADJUSTMENT_LINE_LABEL, isAdjustmentLineType, } from '../../sales-order-lines/adjustment-line-types.js';
import { optionalUuidString } from '../zod-helpers/index.js';
import { BOOK_SALES_ORDER_LINE_TYPE_IDS } from './book-sales-order-line-type-ids.js';
import { SALES_ORDER_LINE_CONSTRAINTS } from './constraints.js';
import { createSalesOrderInputSchema } from './create-input.js';
import { SALES_ORDER_HEADER_CONSTRAINTS } from './header-constraints.js';
import { salesOrderLineQuantitySchema } from './item-quantity.js';
import { MAX_BOOKING_LINES } from './max-booking-lines.js';
import { submitShortfallChoiceSchema } from './submit-shortfall-choice-input.js';
import { duplicateSingleAdjustmentKinds, salesOrderAdjustmentLinesSchema, } from './update-order-input/index.js';
import { ZERO_REVENUE_LINE_TYPES } from './zero-revenue-line-types.js';
// Mirrors addSalesOrderItemInputSchema's per-field rules, minus salesOrderId -- there is no
// order yet at booking time, all lines land on the one being created in the same request.
export const bookSalesOrderLineInputSchema = z
    .object({
    // Adjustment lines (Discount/Shipping/Tax) have no product -- empty string means one of
    // those, enforced below since typeId lives on the same object.
    productId: optionalUuidString('Invalid product selection.'),
    qty: salesOrderLineQuantitySchema,
    // Bounds and sign are enforced below -- a Discount line's amount is stored negative (it
    // reduces the order total), everything else must be non-negative.
    unitPrice: z
        .number()
        .max(1_000_000_000, `${SALES_ORDER_LINE_CONSTRAINTS.unitPrice.label} must be 1,000,000,000 or less.`),
    // Omitted means Sale, matching addSalesOrderItem's existing default.
    typeId: z
        .enum(SalesOrderItemType, `Invalid ${SALES_ORDER_LINE_CONSTRAINTS.lineType.label.toLowerCase()}.`)
        .optional(),
    note: z
        .string()
        .max(SALES_ORDER_LINE_CONSTRAINTS.note.max, `${SALES_ORDER_LINE_CONSTRAINTS.note.label} must be ${SALES_ORDER_LINE_CONSTRAINTS.note.max.toLocaleString('en-US')} characters or less.`)
        .optional(),
    // Omitted means the product's own default UOM, matching addSalesOrderItem's existing default.
    uomId: optionalUuidString(`Invalid ${SALES_ORDER_LINE_CONSTRAINTS.uom.label.toLowerCase()} selection.`)
        .optional()
        .nullable(),
    // Omitted or null means no choice was made; the server refuses that only when the line is short.
    shortfallChoice: submitShortfallChoiceSchema.optional().nullable(),
})
    .superRefine((values, context) => {
    const typeId = values.typeId ?? SalesOrderItemType.SALE;
    if (values.typeId != null && !BOOK_SALES_ORDER_LINE_TYPE_IDS.has(values.typeId)) {
        const typeLabel = SalesOrderItemType[values.typeId] ?? String(values.typeId);
        context.addIssue({
            code: 'custom',
            message: `Line type ${typeLabel} is not valid for booking.`,
            path: ['typeId'],
        });
        return;
    }
    if (isAdjustmentLineType(typeId)) {
        if (values.shortfallChoice != null) {
            context.addIssue({
                code: 'custom',
                message: 'Only product lines take a shortfall choice.',
                path: ['shortfallChoice'],
            });
        }
        // WHY: bookingAdjustmentsFromLegacyLines reads each adjustment as qty * unitPrice, so qty > 1 would inflate it.
        if (values.qty !== 1) {
            context.addIssue({
                code: 'custom',
                message: `${ADJUSTMENT_LINE_LABEL[typeId]} lines must have a quantity of 1.`,
                path: ['qty'],
            });
        }
        if (typeId !== SalesOrderItemType.DISCOUNT && values.unitPrice < 0) {
            context.addIssue({
                code: 'custom',
                message: 'Amount cannot be negative.',
                path: ['unitPrice'],
            });
        }
        // Discount is stored negative (it reduces the order total) -- the client always sends it
        // that way, but that's client convenience, not a server gate. A positive Discount would
        // inflate totalPrice instead of discounting it.
        if (typeId === SalesOrderItemType.DISCOUNT && values.unitPrice > 0) {
            context.addIssue({
                code: 'custom',
                message: 'Discount amount cannot be positive.',
                path: ['unitPrice'],
            });
        }
        return;
    }
    if (!values.productId.trim()) {
        context.addIssue({
            code: 'custom',
            message: `${SALES_ORDER_LINE_CONSTRAINTS.product.label} is required.`,
            path: ['productId'],
        });
    }
    if (values.unitPrice < 0) {
        context.addIssue({
            code: 'custom',
            message: `${SALES_ORDER_LINE_CONSTRAINTS.unitPrice.label} must be a non-negative number.`,
            path: ['unitPrice'],
        });
    }
    if (ZERO_REVENUE_LINE_TYPES.has(typeId) && values.unitPrice !== 0) {
        context.addIssue({
            code: 'custom',
            message: 'Sample and Promo lines must be booked at zero unit price.',
            path: ['unitPrice'],
        });
    }
});
// Extends the header-only createSalesOrderInputSchema with the lines and sample purpose that
// used to be sent via separate mutations (createSalesOrder + N * addSalesOrderItem +
// setSalesOrderSamplePurpose) -- see book-sales-order.ts for why that sequence is replaced by
// one atomic transaction.
export const bookSalesOrderInputSchema = createSalesOrderInputSchema
    .extend({
    samplePurpose: z.enum(SAMPLE_PURPOSES).optional().nullable(),
    internalNotes: z
        .string()
        .max(SALES_ORDER_HEADER_CONSTRAINTS.internalNotes.max, `${SALES_ORDER_HEADER_CONSTRAINTS.internalNotes.label} must be ${SALES_ORDER_HEADER_CONSTRAINTS.internalNotes.max.toLocaleString('en-US')} characters or less.`)
        .optional()
        .nullable(),
    lines: z
        .array(bookSalesOrderLineInputSchema)
        .min(1, 'Add at least one product line.')
        .max(MAX_BOOKING_LINES, `Orders are limited to ${MAX_BOOKING_LINES} lines.`)
        // Adjustment lines still arrive from clients that predate `adjustments`; they are read back into adjustments server-side, which takes one of each kind.
        .superRefine((lines, context) => {
        const adjustmentLines = lines
            .map((line, index) => ({ index, typeId: line.typeId ?? SalesOrderItemType.SALE }))
            .filter((line) => isAdjustmentLineType(line.typeId));
        const refuse = (index, typeId) => context.addIssue({
            code: 'custom',
            message: `Only one ${ADJUSTMENT_LINE_LABEL[typeId]} line is allowed per order.`,
            path: [index, 'typeId'],
        });
        for (const { index, typeId } of duplicateSingleAdjustmentKinds(adjustmentLines.map((line) => line.typeId))) {
            refuse(adjustmentLines[index].index, typeId);
        }
        const taxLines = adjustmentLines.filter((line) => line.typeId === SalesOrderItemType.TAX);
        for (const { index } of taxLines.slice(1))
            refuse(index, SalesOrderItemType.TAX);
    }),
    // Shipping, discount and tax as intent; the server builds the adjustment lines (TOT-8). Payloads without it keep sending adjustment lines.
    adjustments: salesOrderAdjustmentLinesSchema.optional(),
    // true ignores any TAX entries and applies the brand and ship-to state tax config (D16).
    applyTaxDefaults: z.boolean().optional(),
})
    .superRefine((input, context) => {
    if (input.adjustments === undefined)
        return;
    input.lines.forEach((line, index) => {
        if (isAdjustmentLineType(line.typeId ?? SalesOrderItemType.SALE)) {
            context.addIssue({
                code: 'custom',
                message: 'Send shipping, discount and tax as adjustments.',
                path: ['lines', index, 'typeId'],
            });
        }
    });
});
//# sourceMappingURL=book-input.js.map